import React from 'react';
import {
  ComposedChart,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Bar,
  Cell,
  ReferenceLine,
  ReferenceDot,
  Label,
  Line,
  Area,
  CartesianGrid,
} from 'recharts';
import {
  addDays,
  endOfDay,
  format,
  formatDate,
  isSameDay,
  isSameHour,
  isSameMinute,
  startOfDay,
  startOfMonth,
  startOfWeek,
  startOfYear,
  subMonths,
  subWeeks,
  subYears,
} from 'date-fns';
import { fi } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import { cloneDeep } from 'lodash';

import { OmaLummeApp } from '../../omaLummeContext';

import styles from './styles/NordPoolSpotGraph.module.scss';
import tableStyles from './ReportingGraph/styles/ReportingTable.module.scss';

import Button from '../Common/Button';
import DatePicker from '../Common/DatePicker';
import Loading from '../Common/Loading';
import Select from '../Common/Select';
import Switch from '../Common/Switch';
import Tabs from '../Common/Tabs';
import FullScreenButton from '../Common/FullScreenButton';

// Actions.
import { fetchNordPoolSpotData, fetchNordPoolMargins, getWeatherAndForecast } from '../../actions';
// Types.
import { GraphTooltipType, MeteringPointOption } from '../../types/consumptionRates';
import { WeatherDataV2 } from '../../types/contract';
import { NordPoolMargins, NordPoolSpotGraphDatapoint } from '../../types/graphs';
// Utils.
import { setPriceFormat, roundNumber, numberToString } from '../../utils/billing';
import { capitalizeFirstLetter, getCurrent15min, getDateFormat, ReferenceLabel } from '../../utils/consumption';
import { gaEventIfAllowed } from '../../utils/googleAnalytics';
import { generateAddress } from '../../utils/address';
import { useWindowSize } from '../Layout/Header';

const CustomTooltip: React.FunctionComponent<GraphTooltipType> = ({ active, payload }: GraphTooltipType) => {
  const { t } = useTranslation();

  if (active && payload && payload.length > 0) {
    const dataPoint = payload[0].payload;

    return (
      <div className="tooltip-lumme">
        {dataPoint.period?.type && dataPoint.period?.start && (
          <div className="tooltip-title">
            {format(
              dataPoint.period?.start,
              dataPoint.period.type === 'hour' || dataPoint.period.type === 'pt15m'
                ? "dd.MM.yyyy 'klo' HH:mm"
                : getDateFormat(dataPoint.period?.type, true),
              {
                locale: fi,
              },
            )}
          </div>
        )}
        <div>
          <span>{payload[0].name}</span>
          <span>
            {setPriceFormat(roundNumber(payload[0].value / 10)) ?? 0} {t('consumption:nordPoolGraph.spotUnit')}
          </span>
        </div>
        {payload[1] && (
          <div>
            <span>{t(`consumption:consumption.reportingGraph-${payload[1].dataKey}`)}</span>
            <span>
              {roundNumber(payload[1].value, false, 1) ?? 0} {payload[1].unit}
            </span>
          </div>
        )}
        {(dataPoint.temperature || dataPoint.temperatureForecast) && dataPoint.station && (
          <div>
            <span>{t(`consumption:consumption.reportingGraph-${dataPoint.temperature ? 'station' : 'place'}`)}</span>
            <span>{`${dataPoint.station}`}</span>
          </div>
        )}
      </div>
    );
  }
  return null;
};

type NordPoolProps = {
  embed?: boolean;
};

const NordPoolSpotGraph: React.FunctionComponent<NordPoolProps> = (props: NordPoolProps) => {
  const dispatch = OmaLummeApp.useDispatch();
  const { basicData, users, landingPage, selectedMeteringPoint } = OmaLummeApp.useState();

  const customerType = users.activeUser.customerType ?? 'private';

  const { embed } = props;

  const [selectedDate, setSelectedDate] = React.useState<Date | undefined>(new Date());
  const [startDate, setStartDate] = React.useState<Date | undefined>(new Date('2025-02-01'));
  const [endDate, setEndDate] = React.useState<Date | undefined>(new Date());

  const [rawNordPoolData, setRawNordPoolData] = React.useState<NordPoolSpotGraphDatapoint[]>();
  const [nordPoolMargins, setNordPoolMargins] = React.useState<NordPoolMargins>();
  const [nordPoolData, setNordPoolData] = React.useState<NordPoolSpotGraphDatapoint[]>();
  const [weather, setWeather] = React.useState<WeatherDataV2>();
  const [tomorrowDataExists, setTomorrowDataExists] = React.useState<boolean>(false);
  const [resolution, setResolution] = React.useState<string>('hour');
  const [activeTab, setActiveTab] = React.useState<string>(customerType === 'business' ? 'raw' : 'normal');
  const [inclVat, setInclVat] = React.useState<boolean>(customerType === 'business' ? false : true);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [graphType, setGraphType] = React.useState<string>('bar');

  const contract = basicData?.find(
    (d) => d.GSRN === selectedMeteringPoint?.meteringPointId && d.contractNo === selectedMeteringPoint.contractNo.toString(),
  );
  const address = contract ? generateAddress(contract?.meteringpointAddress, false) : undefined;

  const { t } = useTranslation();

  const FinnishTime = "yyyy-MM-dd'T'HH:mm:ss'Z'";

  const windowWidth = useWindowSize()[0];

  React.useEffect(() => {
    let unMounted = false;
    // Create an scoped async function in the hook
    async function fetchGraphPoints(): Promise<void> {
      if (resolution === 'hour' && selectedDate) {
        setLoading(true);
        const data = await fetchNordPoolSpotData(
          format(startOfDay(selectedDate), FinnishTime),
          format(endOfDay(selectedDate), FinnishTime),
          resolution === 'hour' ? 'pt15m' : resolution,
        );

        if (!unMounted && data !== false) {
          setRawNordPoolData(data);
          setLoading(false);
        }
      } else if (startDate && endDate) {
        setLoading(true);
        const data = await fetchNordPoolSpotData(
          format(startOfDay(startDate), FinnishTime),
          format(endOfDay(endDate), FinnishTime),
          resolution === 'hour' ? 'pt15m' : resolution,
        );

        if (!unMounted && data !== false) {
          setRawNordPoolData(data);
          setLoading(false);
        }
      }
    }

    async function fetchMargins(): Promise<void> {
      const data = await fetchNordPoolMargins();

      if (!unMounted) {
        setNordPoolMargins(data);
      }
    }

    async function fetchTomorrowData(): Promise<void> {
      setTomorrowDataExists(false);
      if (selectedDate && isSameDay(selectedDate, new Date())) {
        const nextDay = addDays(selectedDate, 1);
        const tomorrowData = await fetchNordPoolSpotData(
          format(startOfDay(nextDay), FinnishTime),
          format(endOfDay(nextDay), FinnishTime),
          resolution === 'hour' ? 'pt15m' : resolution,
        );

        if (!unMounted && tomorrowData !== false) {
          if (tomorrowData.length >= 92 && tomorrowData.length <= 100) {
            setTomorrowDataExists(true);
          } else {
            setTomorrowDataExists(false);
          }
        }
      }
    }

    async function fetchWeather(): Promise<void> {
      let weatherResolution = 'P1DT';

      if (resolution === 'year') {
        weatherResolution = 'P1MT';
      }
      if (resolution === 'hour') {
        weatherResolution = 'PT15M';
      }

      let start: string | undefined = undefined;
      let end: string | undefined = undefined;

      if (resolution === 'hour' && selectedDate) {
        start = startOfDay(selectedDate).toISOString();
        end = endOfDay(selectedDate).toISOString();
      } else if (startDate && endDate) {
        start = startOfDay(startDate).toISOString();
        end = endOfDay(endDate).toISOString();
      }

      if (address && start && end) {
        const weather = await getWeatherAndForecast(address, start, end, weatherResolution);
        if (weather) {
          setWeather(weather);
        } else {
          setWeather(undefined);
        }
      }
    }

    if (!unMounted) {
      if (
        !(
          rawNordPoolData &&
          rawNordPoolData.length > 0 &&
          ((selectedDate && resolution === 'hour' && isSameDay(new Date(rawNordPoolData[0].period.identifier), selectedDate)) ||
            (startDate && resolution !== 'hour' && isSameDay(new Date(rawNordPoolData[0].period.identifier), startDate)))
        )
      ) {
        fetchGraphPoints();
        if (!nordPoolMargins) {
          fetchMargins();
        }

        if (resolution === 'hour') {
          fetchTomorrowData();
        }
      } else {
        setLoading(false);
      }
      fetchWeather();
    }

    return (): void => {
      unMounted = true;
    };
  }, [selectedDate, startDate, endDate, resolution, address]);

  React.useEffect(() => {
    if (weather && weather.forecast && weather.forecast.length > 0) {
      const filteredForecast = weather.forecast.filter((f) => {
        return f !== undefined && f !== null;
      });

      if (filteredForecast.length > 0) {
        const lastWeather = filteredForecast[filteredForecast.length - 1];

        const lastTemperature = lastWeather.temperature;
        const lastTimestamp = lastWeather.timestamp;
        const currentHourForecast = cloneDeep(filteredForecast[0]);

        if (currentHourForecast && currentHourForecast.timestamp !== lastTimestamp) {
          currentHourForecast.temperature = lastTemperature;
          currentHourForecast.timestamp = lastTimestamp;

          filteredForecast.unshift(currentHourForecast);
        }
      }
    }
  }, [weather]);

  const addMargin = (value: number, type: string, vatRate: number) => {
    let margin = 0;

    if (nordPoolMargins) {
      if (type === 'normal') {
        margin = nordPoolMargins.normal.find((m) => m.vatRate === vatRate)?.margin ?? 0;
      }
      if (type === 'eko') {
        margin = nordPoolMargins.eco.find((m) => m.vatRate === vatRate)?.margin ?? 0;
      }
    }

    return value + margin;
  };

  React.useEffect(() => {
    if (rawNordPoolData && rawNordPoolData.length > 0) {
      const formattedNordPoolData = rawNordPoolData.map((point) => {
        const pointObj: NordPoolSpotGraphDatapoint = {
          period: point.period,
          meanPriceWithVat: addMargin(point.meanPriceWithVat / 10, activeTab, point.vatRate) * 10,
          meanPriceWithoutVat: addMargin(point.meanPriceWithoutVat / 10, activeTab, 0) * 10,
          minPriceWithVat: point.minPriceWithVat,
          minPriceWithoutVat: point.minPriceWithoutVat,
          maxPriceWithVat: point.maxPriceWithVat,
          maxPriceWithoutVat: point.maxPriceWithoutVat,
          vatRate: point.vatRate,
          deliveryArea: point.deliveryArea,
          unit: point.unit,
        };

        if (weather) {
          const weatherObj = weather.data.find(
            (t) =>
              t !== undefined &&
              t !== null &&
              (resolution === 'hour'
                ? isSameHour(new Date(t.time), new Date(point.period.start)) && isSameMinute(new Date(t.time), new Date(point.period.start))
                : isSameDay(new Date(t.time), new Date(point.period.start))),
          );

          if (weatherObj !== undefined) {
            pointObj.temperature = weatherObj.temperatureAvg;
            pointObj.station = weatherObj.location;
          }
        }
        if (weather && weather.forecast) {
          const forecastObj = weather.forecast.find(
            (f) =>
              f !== undefined &&
              f !== null &&
              isSameHour(new Date(f.timestamp), new Date(point.period.identifier)) &&
              isSameMinute(new Date(f.timestamp), new Date(point.period.identifier)),
          );

          if (forecastObj !== undefined) {
            pointObj.temperatureForecast = forecastObj.temperature.value;
            if (!pointObj.station) {
              pointObj.station = weather.location;
            }
          }
        }

        return pointObj;
      });

      setNordPoolData(formattedNordPoolData);
    } else {
      setNordPoolData(rawNordPoolData);
    }
  }, [activeTab, rawNordPoolData, weather]);

  async function downloadCSV(): Promise<void> {
    let csv = '';
    const separator = ';';
    const dateFormat = resolution === 'hour' ? 'yyyy-MM-dd HH:mm' : 'yyyy-MM-dd';

    const csvHeader = [
      t('consumption:consumption.csv-timestamp'),
      `${t('consumption:consumption.csv-price')} ${t('consumption:nordPoolGraph.spotUnit')}`,
    ];

    if (nordPoolData) {
      const csvString = [
        csvHeader,
        ...nordPoolData.map((item) => {
          const price = numberToString((inclVat ? item.meanPriceWithVat : item.meanPriceWithoutVat) / 10);

          const csvBody = [`"${format(item.period.start, dateFormat)}"`, `"${setPriceFormat(price)}"`];

          return csvBody;
        }),
      ]
        .map((e) => e.join(separator))
        .join('\n');

      csv += csvString;

      const file = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=UTF-8' });
      const fileUrl = URL.createObjectURL(file);

      const invisibleCsvLink = document.createElement('a');
      document.body.appendChild(invisibleCsvLink);
      invisibleCsvLink.href = fileUrl;
      invisibleCsvLink.download = `nordpool_${
        resolution !== 'hour' && startDate && endDate
          ? `${format(startDate, 'yyyy-MM-dd')}_${format(endDate, 'yyyy-MM-dd')}`
          : `${format(selectedDate || new Date(), 'yyyy-MM-dd')}`
      }_${resolution}_${inclVat ? 'vat' : 'noVat'}_${activeTab}.csv`;
      invisibleCsvLink.click();
    }
  }

  const current15min = getCurrent15min();

  const currentDataPoint =
    nordPoolData && nordPoolData.length > 0 ? nordPoolData.find((d) => isSameMinute(d.period.start, new Date(current15min))) : undefined;
  const currentValue = nordPoolData && currentDataPoint ? currentDataPoint[inclVat ? 'meanPriceWithVat' : 'meanPriceWithoutVat'] : 0;
  const dataMinValue =
    nordPoolData && nordPoolData.length > 0 ? Math.min(...nordPoolData.map((item) => item[inclVat ? 'meanPriceWithVat' : 'meanPriceWithoutVat'])) : 0;
  const dataAvgValue =
    nordPoolData && nordPoolData.length > 0
      ? nordPoolData.reduce((a, b) => a + b[inclVat ? 'meanPriceWithVat' : 'meanPriceWithoutVat'], 0) / nordPoolData.length
      : 0;
  const dataMaxValue =
    nordPoolData && nordPoolData.length > 0 ? Math.max(...nordPoolData.map((item) => item[inclVat ? 'meanPriceWithVat' : 'meanPriceWithoutVat'])) : 0;
  const currentPrice = setPriceFormat(roundNumber(currentValue / 10));
  const dataMin = setPriceFormat(roundNumber(dataMinValue / 10));
  const dataAvg = setPriceFormat(roundNumber(dataAvgValue / 10));
  const dataMax = setPriceFormat(roundNumber(dataMaxValue / 10));

  React.useEffect(() => {
    if (
      nordPoolData &&
      resolution === 'hour' &&
      currentDataPoint &&
      startOfDay(currentDataPoint?.period.start).getTime() === startOfDay(new Date()).getTime() &&
      landingPage.spotPricesToday !== nordPoolData
    ) {
      dispatch(['changeLandingPageSpotPricesToday', nordPoolData]);
    }
  }, [nordPoolData, resolution, currentDataPoint, landingPage.spotPricesToday]);

  const nordPoolTabs = [
    {
      label: t('consumption:nordPoolGraph.tabNormal'),
      key: 'normal',
    },
    {
      label: t('consumption:nordPoolGraph.tabEko'),
      key: 'eko',
    },
    {
      label: t('consumption:nordPoolGraph.tabRaw'),
      key: 'raw',
    },
  ];

  const today = new Date();
  const resolutionTabs = [
    {
      label: t('consumption:nordPoolGraph.dayHourly'),
      key: 'hour',
    },
    {
      label: t('consumption:nordPoolGraph.dayAvg'),
      key: 'day',
    },
    {
      label: t('consumption:nordPoolGraph.weekAvg'),
      key: 'week',
    },
    {
      label: t('consumption:nordPoolGraph.monthAvg'),
      key: 'month',
    },
    {
      label: t('consumption:nordPoolGraph.yearAvg'),
      key: 'year',
    },
  ];

  const graphTypes: MeteringPointOption[] = [
    {
      value: 'bar',
      label: t(`consumption:graphs.graphTypeBar`),
    },
    {
      value: 'area',
      label: t(`consumption:graphs.graphTypeArea`),
    },
    {
      value: 'table',
      label: t(`consumption:graphs.graphTypeTable`),
    },
  ];

  React.useEffect(() => {
    if (resolution === 'hour') {
      setSelectedDate(new Date());
    }
    if (resolution === 'day') {
      setStartDate(startOfDay(subWeeks(new Date(), 2)));
      setEndDate(new Date());
    }
    if (resolution === 'week') {
      setStartDate(startOfWeek(subWeeks(new Date(), 14), { weekStartsOn: 1 }));
      setEndDate(new Date());
    }
    if (resolution === 'month') {
      setStartDate(startOfMonth(subMonths(new Date(), 11)));
      setEndDate(new Date());
    }
    if (resolution === 'year') {
      setStartDate(startOfYear(subYears(new Date(), 5)));
      setEndDate(new Date());
    }
  }, [resolution]);

  const maxDot = nordPoolData && nordPoolData.find((d) => d.meanPriceWithVat === dataMaxValue);
  const minDot = nordPoolData && nordPoolData.find((d) => d.meanPriceWithVat === dataMinValue);

  return (
    <div id="spotPrices" className={`graph-container ${styles.nordPoolGraph}`}>
      {!embed && <FullScreenButton elementId="spotPrices" position="topRight" />}
      <h2 className={styles.title}>{t('consumption:nordPoolGraph.nordPoolTitle')}</h2>
      <p className={styles.ingress}>{t('consumption:nordPoolGraph.nordPoolIngress')}</p>
      <header className={styles.header}>
        <Tabs tabs={nordPoolTabs} active={{ activeTab, setActiveTab }} />

        <div className={styles.dateLegend}>
          <div>
            {resolution === 'hour' && (
              <>
                <DatePicker
                  prevNext
                  type="single"
                  day={{ date: selectedDate, setDate: setSelectedDate }}
                  limits={{ minDate: undefined, maxDate: tomorrowDataExists ? addDays(today, 1) : today }}
                />
              </>
            )}

            {resolution !== 'hour' && (
              <DatePicker
                prevNext
                type="range"
                dates={{ startDate: startDate, setStartDate: setStartDate, endDate: endDate, setEndDate: setEndDate }}
                limits={{
                  minDate: undefined,
                  maxDate: tomorrowDataExists ? addDays(today, 1) : today,
                  maxLength: resolution === 'day' ? 31 : resolution === 'month' ? 366 : undefined,
                }}
                resolution={{ resolution: resolution, setResolution: setResolution }}
              />
            )}
          </div>
        </div>
      </header>
      <Tabs tabs={resolutionTabs} active={{ activeTab: resolution, setActiveTab: setResolution }} />
      {loading && <Loading size="small" />}
      {nordPoolData && !loading && selectedDate && (
        <div className={styles.graphLegend}>
          {resolution === 'hour' && isSameDay(selectedDate, new Date()) && (
            <div className={styles.priceNow}>
              {t('consumption:nordPoolGraph.priceNow')} ({t('consumption:consumption.reportingGraph-timeAt')}{' '}
              {formatDate(currentDataPoint?.period.start ? new Date(currentDataPoint?.period.start) : new Date(), 'HH:mm')})
              <span>
                {currentPrice}&nbsp;{t('consumption:nordPoolGraph.spotUnit')}
              </span>
            </div>
          )}
          <div className={styles.minMax}>
            <div className={styles.min}>
              {t(resolution === 'hour' ? 'consumption:nordPoolGraph.dayMinPrice' : 'consumption:nordPoolGraph.dataMinPrice')}: {dataMin}&nbsp;
              {t('consumption:nordPoolGraph.spotUnit')}
            </div>
            <div className={styles.max}>
              {t(resolution === 'hour' ? 'consumption:nordPoolGraph.dayMaxPrice' : 'consumption:nordPoolGraph.dataMaxPrice')}: {dataMax}&nbsp;
              {t('consumption:nordPoolGraph.spotUnit')}
            </div>
          </div>

          <Select options={graphTypes} activeOption={graphType} setActiveOption={setGraphType} />
        </div>
      )}
      {loading && <Loading />}
      {nordPoolData && !loading && (
        <div className="graph-wrapper">
          {graphType !== 'table' && (
            <ResponsiveContainer width={'100%'} height={300}>
              <ComposedChart data={nordPoolData} margin={{ top: 25, right: 0, bottom: 0, left: 0 }}>
                <defs>
                  <linearGradient id="colorSpotPrice" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor="#7FB722" stopOpacity={1} />
                    <stop offset="100%" stopColor="#7FB722" stopOpacity={0.1} />
                  </linearGradient>
                </defs>
                <CartesianGrid vertical={false} stroke="#e9e9e9" />
                <XAxis
                  dataKey="period.start"
                  stroke="#8E9295"
                  interval={
                    windowWidth >= 768
                      ? resolution === 'hour'
                        ? 3
                        : resolution === 'day'
                          ? 1
                          : 0
                      : resolution === 'year'
                        ? 0
                        : resolution === 'hour'
                          ? 7
                          : 3
                  }
                  tickFormatter={(tick: string) => {
                    return format(new Date(tick), getDateFormat(resolution, true), { locale: fi });
                  }}
                />
                <YAxis
                  scale="linear"
                  interval="preserveStartEnd"
                  stroke="#8E9295"
                  tickFormatter={(tick: number) => {
                    return (tick / 10).toString().replace('.', ',');
                  }}
                >
                  <Label value={`${t('consumption:nordPoolGraph.spotUnit')}`} offset={0} position="insideTopLeft" fill="#000000" dx={20} dy={-25} />
                </YAxis>
                {weather && ((weather.data?.length ?? 0) > 0 || (weather.forecast?.length ?? 0) > 0) && (
                  <YAxis yAxisId={1} orientation="right" unit=" °C" strokeWidth={0} />
                )}
                <Tooltip content={<CustomTooltip />} wrapperStyle={{ width: 300 }} />
                {nordPoolData && nordPoolData.length > 0 && (
                  <>
                    {graphType === 'bar' && (
                      <Bar
                        name={`${t(`contracts:contracts.spotPrice`)}`}
                        dataKey={inclVat ? 'meanPriceWithVat' : 'meanPriceWithoutVat'}
                        radius={[50, 50, 0, 0]}
                      >
                        {nordPoolData.map((entry, index) => {
                          let fill = '#7fb722';

                          if (nordPoolData[index][inclVat ? 'meanPriceWithVat' : 'meanPriceWithoutVat'] === dataMinValue) {
                            fill = '#c0e300';
                          }
                          if (nordPoolData[index][inclVat ? 'meanPriceWithVat' : 'meanPriceWithoutVat'] === dataMaxValue) {
                            fill = '#004107';
                          }
                          const indexStart = format(new Date(nordPoolData[index].period.start), FinnishTime);
                          if (resolution === 'hour') {
                            if (indexStart === format(current15min, FinnishTime)) {
                              fill = '#e0ccff';
                            }
                          }

                          return <Cell fill={fill} key={`nordpool-fill-${index}`} />;
                        })}
                      </Bar>
                    )}
                    {graphType === 'area' && (
                      <>
                        <Area
                          type="monotone"
                          name={`${t(`contracts:contracts.spotPrice`)}`}
                          dataKey={inclVat ? 'meanPriceWithVat' : 'meanPriceWithoutVat'}
                          fill="url(#colorSpotPrice)"
                          stroke="#66921B"
                          strokeWidth={2}
                        />

                        <ReferenceDot r={4} x={maxDot?.period.start} y={maxDot?.meanPriceWithVat} stroke="#666B6D" strokeWidth={2} fill="#FFFFFF" />
                        <ReferenceDot r={4} x={minDot?.period.start} y={minDot?.meanPriceWithVat} stroke="#C0E300" strokeWidth={2} fill="#404446" />
                        {resolution === 'hour' &&
                          currentDataPoint?.period.start &&
                          isSameDay(new Date(currentDataPoint?.period.start), new Date()) && (
                            <ReferenceLine x={currentDataPoint?.period.start} stroke="#e0ccff" strokeWidth={2} />
                          )}
                      </>
                    )}
                    {weather && weather.data.length > 0 && (
                      <Line yAxisId={1} type="monotone" dot={false} dataKey="temperature" unit=" °C" stroke="#004107" strokeWidth={2} />
                    )}
                    {weather && weather.forecast.length > 0 && (
                      <Line
                        yAxisId={1}
                        type="monotone"
                        dot={false}
                        dataKey="temperatureForecast"
                        unit=" °C"
                        stroke="#004107"
                        strokeDasharray="8 4"
                        strokeWidth={2}
                      />
                    )}
                    <ReferenceLine
                      y={dataAvgValue}
                      stroke="#666B6D"
                      strokeWidth={1}
                      strokeDasharray="3 3"
                      label={
                        <ReferenceLabel
                          value={`${t(`consumption:nordPoolGraph.dataAvgPrice`)}: ${dataAvg} ${t('consumption:nordPoolGraph.spotUnit')}`}
                        />
                      }
                    />
                  </>
                )}
              </ComposedChart>
            </ResponsiveContainer>
          )}
          {graphType === 'table' && (
            <div className={styles.tableWrapper}>
              <table className={tableStyles.table}>
                <thead>
                  <tr>
                    <th>{t('consumption:consumption.csv-timestamp')}</th>
                    {inclVat && <th>{t('consumption:consumption.csv-costWithVat')}</th>}
                    {!inclVat && <th>{t('consumption:consumption.csv-costWithoutVat')}</th>}
                    {weather && <th>{t('consumption:consumption.csv-temperature')}</th>}
                  </tr>
                </thead>
                <tbody>
                  {nordPoolData.map((item) => {
                    let timeFormat = 'iiiiii dd.MM.yyyy';
                    if (resolution === 'year') {
                      timeFormat = 'yyyy';
                    }
                    if (resolution === 'month') {
                      timeFormat = 'LLLL yyyy';
                    }
                    if (resolution === 'week') {
                      timeFormat = 'w/yyyy';
                    }

                    return (
                      <tr key={item.period.identifier}>
                        <td>
                          {capitalizeFirstLetter(format(new Date(item.period.start), timeFormat, { locale: fi }))}{' '}
                          {resolution === 'hour' &&
                            `${t(`consumption:consumption.reportingGraph-timeAt`)} ${format(new Date(item.period.start), 'HH:mm')}`}
                        </td>
                        {inclVat && (
                          <td>
                            {setPriceFormat(roundNumber(item.meanPriceWithVat ? item.meanPriceWithVat / 10 : 0))}{' '}
                            {t('consumption:nordPoolGraph.spotUnit')}
                          </td>
                        )}
                        {!inclVat && (
                          <td>
                            {setPriceFormat(roundNumber(item.meanPriceWithoutVat ? item.meanPriceWithoutVat / 10 : 0))}{' '}
                            {t('consumption:nordPoolGraph.spotUnit')}
                          </td>
                        )}
                        {weather && <td>{item.temperature ? `${roundNumber(item.temperature, false)} °C` : ''}</td>}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <footer className={styles.footer}>
            <form className={styles.vatSelector}>
              <Switch
                offLabel={t('consumption:nordPoolGraph.exclVat')}
                onLabel={t('consumption:nordPoolGraph.inclVat')}
                onChange={(): void => setInclVat(!inclVat)}
                active={inclVat}
              />
            </form>
            <p>
              {!inclVat && activeTab === 'raw' && t('consumption:nordPoolGraph.rawPrice')}
              {!inclVat &&
                activeTab !== 'raw' &&
                t('consumption:nordPoolGraph.rawPriceWithMargin', {
                  contractName: t(`consumption:nordPoolGraph.tab${activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}`),
                })}
              {inclVat && activeTab === 'raw' && t('consumption:nordPoolGraph.vatPriceNoMargin')}
              {inclVat &&
                activeTab !== 'raw' &&
                t('consumption:nordPoolGraph.vatPriceWithMargin', {
                  contractName: t(`consumption:nordPoolGraph.tab${activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}`),
                })}
            </p>
            {!embed && nordPoolData && nordPoolData.length > 0 && (
              <Button
                className="download"
                onClick={(e): void => {
                  e.preventDefault();
                  gaEventIfAllowed({
                    category: 'Lataa Nordpool CSV',
                    action: 'Lataa Nordpool CSV',
                  });
                  downloadCSV();
                }}
              >
                {`${t('consumption:consumption.download')} CSV`}
              </Button>
            )}
          </footer>
        </div>
      )}
    </div>
  );
};

export default NordPoolSpotGraph;
